import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import api from '../utils/api'
import InvoiceFirstStep from '../components/HourlyInvoice/InvoiceFirstStep'
import InvoiceSecondStep from '../components/HourlyInvoice/InvoiceSecondStep'
import InvoiceSummary from '../components/HourlyInvoice/InvoiceSummary'
import InvoicePreview from '../components/HourlyInvoice/InvoicePreview'
import useAdminFee, { computeReminderList, calculateStripeProcessingFee } from '../components/HourlyInvoice/useAdminFee'

/**
 * /hourly-billing/:caseId — full port of Scale Portal's hourly billing flow.
 *
 * Steps:
 *   1. Invoice Recipient (Dialog) — radio cards + party detail cards
 *   2. Invoice Details (Dialog)   — line items + due date + reminders + notes
 *   3. Preview / Send (full page) — split summary + Stripe-style invoice
 *
 * Backend:
 *   - GET  /cases/:id/hourly-billing/details        — page bootstrap data
 *   - POST /cases/:id/hourly-billing/drafts         — create/upsert invoice_history
 *   - GET  /cases/:id/hourly-billing/drafts/:uuid   — load existing draft
 *   - POST /cases/:id/hourly-billing/send           — issue Stripe invoices + email
 */
export default function CreateInvoice() {
    const { caseId } = useParams()
    const navigate = useNavigate()

    /* Lock the page behind the editor.
     *
     * Case Manager keeps case-detail mounted underneath this route (App.js
     * passes a backgroundLocation), so the document body is still the scroll
     * container and the page scrolled away behind the dialog. Scale does not
     * have this problem because its UI-kit Dialog sets the same style, so the
     * two portals behaved differently on the same action.
     *
     * Restores whatever was there rather than clearing it, so a parent that
     * had already locked scrolling keeps its lock when this unmounts. */
    useEffect(() => {
        const previous = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = previous
        }
    }, [])

    const [searchParams] = useSearchParams()
    const draftUuid = searchParams.get('draft_invoice')

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [bootstrap, setBootstrap] = useState(null)
    const [step, setStep] = useState(draftUuid ? 3 : 1)
    // submitting: false | 'draft' | 'email' — distinguishes which final button is in flight (Scale parity).
    const [submitting, setSubmitting] = useState(false)
    // Lock to prevent double-draft creation on fast double-click of Continue / Preview.
    const [persistingDraft, setPersistingDraft] = useState(false)
    const [toast, setToast] = useState(null)

    // Generate INV-YYYY-NNN like Scale (initialValues line 292) when not loading a draft.
    const genInvoiceNumber = () => `INV-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000)}`
    // Generate a client-side UUID up front so retries on the first persistDraft
    // call don't create duplicate ghost drafts (BE upsertHourlyDraft uses
    // body.uuid as idempotency key).
    const genUuid = () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID()
        }
        // Fallback for older browsers
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8
            return v.toString(16)
        })
    }

    const [values, setValues] = useState({
        invoiceUuid: draftUuid ? null : genUuid(),
        selectedOption: 'all',
        selectedParty: null,
        invoice_allocation_party: null,
        items: [],
        due_date: '',
        reminders: {},
        additional_notes: '',
        invoice_number: draftUuid ? '' : genInvoiceNumber(),
        clientForGscCase: null,
    })
    const setFieldValue = (k, v) => setValues((p) => ({ ...p, [k]: v }))

    // Step 1 inline edits land here, keyed by client_id. Merged into allClients
    // before draft upsert + send so edits actually persist (Scale parity).
    // Also: persist to the DB via PUT /cases/:id/clients/:clientId so reload
    // / Step 3 / Finance Table all reflect the change immediately.
    const [clientOverrides, setClientOverrides] = useState({})
    const handleClientChange = (clientId, patch) => {
        const ci = patch?.client_information || {}
        setClientOverrides((prev) => ({
            ...prev,
            [clientId]: {
                ...(prev[clientId] || {}),
                ...ci,
                invoice_emails: patch?.invoice_emails ?? (prev[clientId]?.invoice_emails ?? null),
            },
        }))
        // Fire-and-forget DB persist — only on the fields the BE understands.
        const payload = {}
        if (ci.name !== undefined) payload.name = ci.name
        if (ci.email !== undefined) payload.email = ci.email
        if (ci.phone_number !== undefined) payload.phone_number = ci.phone_number
        if (ci.address !== undefined) payload.address = ci.address
        if (Object.keys(payload).length > 0 && caseId && clientId) {
            api.put(`/cases/${caseId}/clients/${clientId}`, payload)
                .then(() => setToast({ type: 'success', message: 'Client updated' }))
                .catch((err) => {
                    const msg = err?.response?.data?.message || 'Failed to save client changes'
                    setToast({ type: 'error', message: msg })
                })
        }
    }
    const applyOverrides = (c) => {
        if (!c) return c
        const o = clientOverrides[c.client_id]
        if (!o) return c
        return { ...c, ...o }
    }

    useEffect(() => {
        const t = setTimeout(() => setToast(null), 4000)
        return () => clearTimeout(t)
    }, [toast])

    // Bootstrap — load case + mediator + parties.
    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const { data } = await api.get(`/cases/${caseId}/hourly-billing/details`)
                if (cancelled) return
                setBootstrap(data?.data || null)
            } catch (err) {
                setError(err?.response?.data?.message || 'Failed to load case')
            } finally {
                setLoading(false)
            }
        })()
        return () => { cancelled = true }
    }, [caseId])

    // Load draft if URL has ?draft_invoice=<uuid>. Cancellation-guarded.
    useEffect(() => {
        if (!draftUuid || !caseId) return undefined
        let cancelled = false
        ;(async () => {
            try {
                const { data } = await api.get(`/cases/${caseId}/hourly-billing/drafts/${draftUuid}`)
                if (cancelled) return
                const d = data?.data
                if (!d) return
                setValues((p) => ({
                    ...p,
                    invoiceUuid: d.uuid,
                    selectedOption: d.selectedOption || 'all',
                    selectedParty: d.selectedParty || null,
                    invoice_allocation_party: d.invoice_allocation_party || null,
                    items: Array.isArray(d.items) ? d.items : (typeof d.items === 'string' ? JSON.parse(d.items || '[]') : []),
                    due_date: d.due_date || '',
                    reminders: d.reminders || {},
                    additional_notes: d.additional_notes || '',
                    invoice_number: d.invoice_number || '',
                }))
            } catch (err) {
                if (!cancelled) setToast({ type: 'error', message: 'Failed to load draft' })
            }
        })()
        return () => { cancelled = true }
    }, [draftUuid, caseId])

    const mediator = bootstrap?.mediator
    const mediatorSettings = mediator?.mediatorSettings || {}
    const isGscCase = !!bootstrap?.case?.is_gsc_case
    const isCouponApplied = !!bootstrap?.case?.coupon_id
    const adminFeeCollected = (bootstrap?.plaintiffPayments?.[0]?.amount > 0) || (bootstrap?.defendentPayments?.[0]?.amount > 0)

    // The allocation this invoice is actually billed under. A draft's own value
    // wins; otherwise fall back to the MEDIATOR's default, exactly as the
    // mediator portal does (HourlyInvoice/index.jsx:356-360).
    //
    // WHY THIS MATTERS, AND WHY null IS NOT A SAFE DEFAULT:
    // scale-be bills the FULL subtotal to EVERY party when this arrives null
    // (stripeRoutes.js:429-431 — the "single-invoice / no-allocation" branch),
    // instead of each party's share. The mediator portal essentially never hits
    // that branch because it always resolves the mediator default, and the
    // mediators column itself defaults to 'equally_split'. This wizard had no
    // fallback at all, and the toggle that could set a value only renders when
    // BOTH sides have more than one party — so an ordinary two-party split
    // invoice sent null and charged each party 100% of the subtotal.
    //
    // Derived rather than seeded into `values` because the form initialises
    // before `bootstrap` resolves, and because the toggle must still win when
    // the user touches it.
    const effectiveAllocationParty =
        values.invoice_allocation_party ?? mediator?.invoice_allocation_party ?? null

    // The card fee is collected by the payment GATEWAY at the moment the party
    // pays, and only if they choose card — an ACH payer owes nothing extra. It
    // is never an invoice line item, so the preview must not show one.
    //
    // This previously passed the mediator's raw `processing_fee_to_client`
    // straight through, so the row appeared for every such mediator — showing
    // the case manager a figure that is NOT on the invoice actually sent, and
    // computing it at 2.2% where the rest of the codebase uses Stripe's
    // 2.9% + $0.30. `useAdminFee`'s own DISABLE_INVOICE_PROCESSING_FEE already
    // zeroes the persisted value; only this render path still disagreed.
    //
    // The mediator portal reaches the same place by asking the backend whether
    // the gateway collects it (GET /invoice/config/processing-fee, which
    // defaults to true), and its own comment is unambiguous: "Processing fee is
    // ALWAYS deferred to the payment gateway ... never an invoice line item."
    const showProcessingFeeRow = false

    // Seed the form with that default once the mediator resolves, so the
    // allocation toggle SHOWS the option the invoice will actually be billed
    // under. Without this the radio reads as "nothing selected" on a case where
    // the toggle renders, while the payload silently carries the default.
    // A value already set — by the user or by a loaded draft — is never
    // overwritten.
    useEffect(() => {
        const fallback = mediator?.invoice_allocation_party
        if (!fallback) return
        setValues((p) =>
            p.invoice_allocation_party == null
                ? { ...p, invoice_allocation_party: fallback }
                : p
        )
    }, [mediator?.invoice_allocation_party])

    const effectiveHourlyRate = useMemo(() => {
        if (isGscCase && mediatorSettings.gsc_hourly_rate != null) return Number(mediatorSettings.gsc_hourly_rate) || 0
        // BE pre-computes pricing-tier rate when totalParties > 2 (Scale parity).
        if (bootstrap?.effectiveHourlyRate != null) return Number(bootstrap.effectiveHourlyRate) || 0
        return Number(mediator?.mediationRates?.hourly_rate || mediator?.hourly_rate || 0)
    }, [isGscCase, mediator, mediatorSettings, bootstrap])

    const subtotal = (values.items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0)

    const allClients = useMemo(() => {
        if (!bootstrap) return []
        const tagRole = (c, role) => (c ? applyOverrides({ ...c, role }) : null)
        const tagAdditional = (p, role) => applyOverrides({ client_id: p.client_id, name: p.name, email: p.email, phone: p.phone, role })
        return [
            tagRole(bootstrap.plaintiff, 'plaintiff'),
            ...((bootstrap.additionalPlaintiffParties || []).map((p) => tagAdditional(p, 'plaintiff'))),
            tagRole(bootstrap.defendant, 'defendant'),
            ...((bootstrap.additionalDefendantParties || []).map((p) => tagAdditional(p, 'defendant'))),
        ].filter((c) => c && c.client_id)
    // eslint-disable-next-line
    }, [bootstrap, clientOverrides])

    const adminFeeData = useAdminFee({
        subtotal,
        selectedOption: values.selectedOption,
        selectedParty: values.selectedParty,
        invoiceAllocationParty: effectiveAllocationParty,
        clients: allClients,
        selectedClientId: values.selectedParty || bootstrap?.plaintiff?.client_id,
        mediatorRecord: mediator,
        isGscCase,
        isAdminFeeCollected: adminFeeCollected,
        isCouponApplied,
        mediatorProcessingFeeToClient: !!mediator?.mediationRates?.processing_fee_to_client,
    })

    // Persist a draft. Returns the upserted row.
    const persistDraft = async (overrideStatus) => {
        const payload = {
            uuid: values.invoiceUuid || undefined,
            mediator_id: mediator?.mediator_id || null,
            items: values.items,
            subtotal,
            processing_fee: adminFeeData.displayProcessingFee || 0,
            grand_total: adminFeeData.displayTotal || subtotal,
            status: overrideStatus || 'draft',
            due_date: values.due_date || null,
            invoice_number: values.invoice_number || null,
            send_from: mediator
                ? { name: `${mediator.first_name || ''} ${mediator.last_name || ''}`.trim(), email: mediator.email, phone: mediator.phone }
                : null,
            send_to: (values.selectedOption === 'one' && values.selectedParty
                ? allClients.filter((c) => c.client_id === values.selectedParty)
                : allClients
            ).map((c) => ({
                client_id: c.client_id,
                name: c.name,
                email: c.email,
                role: c.role,
                amount: values.selectedOption === 'one' ? subtotal : adminFeeData.perPartyShare,
                processing_fee: adminFeeData.displayProcessingFee,
                percentage: values.selectedOption === 'one' ? '100%' : `${adminFeeData.invoiceAllocationData.percentage}%`,
            })),
            selectedOption: values.selectedOption,
            selectedParty: values.selectedParty != null ? String(values.selectedParty) : null,
            reminders: values.reminders,
            additional_notes: values.additional_notes,
            admin_fee: adminFeeData.adminFeeAmountNumeric || 0,
            invoice_allocation_party: effectiveAllocationParty,
        }
        const { data } = await api.post(`/cases/${caseId}/hourly-billing/drafts`, payload)
        const row = data?.data
        if (row?.uuid) setFieldValue('invoiceUuid', row.uuid)
        return row
    }

    const handleContinueFromStep1 = async () => {
        if (persistingDraft) return // double-click guard
        if (values.selectedOption === 'one' && !values.selectedParty) {
            setToast({ type: 'error', message: 'Please select a party' })
            return
        }
        try {
            setPersistingDraft(true)
            await persistDraft('draft')
            setStep(2)
        } catch (err) {
            setToast({ type: 'error', message: err?.response?.data?.message || 'Failed to save draft' })
        } finally {
            setPersistingDraft(false)
        }
    }

    const handlePreviewFromStep2 = async () => {
        if (persistingDraft) return // double-click guard
        // Force-blur the active element so HoursInput / number inputs commit
        // their pending values to React state before we read them. Without
        // this, clicking Preview right after typing hours leaves
        // `quantity`/`amount` stale and `subtotal <= 0` blocks the step.
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur()
        }
        // Yield a tick so the resulting onChange propagates through React.
        await new Promise((r) => setTimeout(r, 30))

        const latestItems = (values.items) || []
        const latestSubtotal = latestItems.reduce((acc, it) => acc + (Number(it.amount) || 0), 0)
        if (!values.due_date) { setToast({ type: 'error', message: 'Due date required' }); return }
        if (!latestItems.length || latestItems.some((it) => !it.description) || latestSubtotal <= 0) {
            setToast({ type: 'error', message: 'Each line item needs a description and amount > 0' })
            return
        }
        try {
            setPersistingDraft(true)
            await persistDraft('draft')
            setStep(3)
        } catch (err) {
            setToast({ type: 'error', message: err?.response?.data?.message || 'Failed to save' })
        } finally {
            setPersistingDraft(false)
        }
    }

    const handleFinalize = async (intent /* 'draft' | 'email' */) => {
        try {
            setSubmitting(intent)
            const row = await persistDraft(intent === 'email' ? 'sendviaemail' : 'draft')
            if (intent === 'email') {
                if (!row?.id) {
                    throw new Error('Could not persist invoice draft — please try again')
                }
                const targetClientIds = values.selectedOption === 'one' && values.selectedParty
                    ? [Number(values.selectedParty)]
                    : allClients.map((c) => c.client_id)
                // Scale parity (index.jsx:622-627): admin_fee_after_processing_fee
                // = adminFee - calculateStripeProcessingFee(adminFee). Use the shared
                // helper (2.9% + $0.30) so this matches useAdminFee exactly — it
                // previously hardcoded 2.2% and diverged from Scale on every invoice.
                const adminFee = Number(adminFeeData.adminFeeAmountNumeric) || 0
                const adminFeeProcFee = adminFee > 0 ? calculateStripeProcessingFee(adminFee) : 0
                const adminFeeAfterProcFee = Math.max(0, adminFee - adminFeeProcFee)

                const sendRes = await api.post(`/cases/${caseId}/hourly-billing/send`, {
                    client_id: targetClientIds,
                    invoicePaymentId: row?.id,
                    selectedOption: values.selectedOption,
                    reminders: values.reminders,
                    admin_fee: adminFee,
                    admin_fee_after_processing_fee: adminFeeAfterProcFee,
                    is_admin_fee_added_in_line_item: !!adminFeeData.is_admin_fee_added_in_line_item,
                    is_gsc_case: !!isGscCase,
                    mediator_uuid: mediator?.user_id || null,
                    client_information: allClients.map((c) => ({ client_id: c.client_id, name: c.name, email: c.email })),
                })

                // Scale parity (index.jsx:641-654): GSC cases override email
                // payload's client_id with the Stripe-assigned ID before
                // calling /invoice/send-hourly-invoice-email.
                if (isGscCase) {
                    const stripeFirstResult = sendRes?.data?.data?.results?.[0] || sendRes?.data?.results?.[0]
                    const gscClientIdFromStripe = stripeFirstResult?.client_id || stripeFirstResult?.clientData?.client_id
                    if (gscClientIdFromStripe) {
                        // Backend already proxies the email send. We log here
                        // for diagnostic; if the backend exposes a separate
                        // email endpoint in future, call it with this id.
                        // eslint-disable-next-line no-console
                        console.info('[GSC] using Stripe-assigned client_id', gscClientIdFromStripe)
                    }
                }
                setToast({ type: 'success', message: 'Invoice Sent Successfully' })
                setTimeout(() => navigate(`/cases/${caseId}`, { state: { refresh: true } }), 700)
            } else {
                setToast({ type: 'success', message: 'Invoice Saved as Draft' })
                setTimeout(() => navigate(`/cases/${caseId}`, { state: { refresh: true } }), 700)
            }
        } catch (err) {
            setToast({ type: 'error', message: err?.response?.data?.message || 'Failed' })
        } finally {
            setSubmitting(false)
        }
    }

    // While data is loading, render only the modal frame with an inline
    // loader. No page-level progress bar — the case detail page behind
    // stays clean, just blurred.
    if (loading || !bootstrap || error) {
        return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xl">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1180px] max-h-[92vh] overflow-hidden relative">
                    <button type="button" onClick={() => navigate(`/cases/${caseId}`)} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 z-50" aria-label="Close">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 5l10 10M15 5L5 15"/></svg>
                    </button>
                    <div className="px-6 pt-5 pb-3">
                        <h2 className="text-lg font-semibold text-gray-900">Create Invoice</h2>
                        <p className="text-sm text-gray-500 mt-0.5">Select who will receive this invoice.</p>
                        <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-1.5">
                                {[1, 2, 3].map((n) => (
                                    <div key={n} className="h-2 w-8 rounded-full" style={n === 1 ? { backgroundColor: '#115E59' } : { backgroundColor: '#E5E7EB' }} />
                                ))}
                            </div>
                            <span className="text-sm text-gray-600 whitespace-nowrap">Step 1 of 3</span>
                        </div>
                    </div>
                    <div className="px-6 py-16 flex items-center justify-center">
                        {error ? (
                            <p className="text-sm text-red-600">{error}</p>
                        ) : (
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-200 border-t-[#115E59] animate-spin" />
                                Loading…
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    // Allocation toggle (Scale parity InvoiceSecondStep:497) shows when both
    // sides have more than one party — i.e. plaintiff side count > 1 AND
    // defendant side count > 1.
    const plaintiffCount = (bootstrap.plaintiff ? 1 : 0) + (bootstrap.additionalPlaintiffParties?.length || 0)
    const defendantCount = (bootstrap.defendant ? 1 : 0) + (bootstrap.additionalDefendantParties?.length || 0)
    const showAllocation = plaintiffCount > 1 && defendantCount > 1

    // Full-width step row — pills on left, "Step N of 3" on far right.
    // Single-active highlight (Scale parity): only the current step is teal.
    const stepRow = (
        <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">
                {[1, 2, 3].map((n) => (
                    <div
                        key={n}
                        className="h-2 w-8 rounded-full"
                        style={step === n ? { backgroundColor: '#115E59' } : { backgroundColor: '#E5E7EB' }}
                    />
                ))}
            </div>
            <span className="text-sm text-gray-600 whitespace-nowrap">Step {step} of 3</span>
        </div>
    )

    const recipientLine = (() => {
        if (values.selectedOption === 'one') {
            const p = allClients.find((c) => c.client_id === values.selectedParty)
            return p ? `${p.name} (${p.email})` : '—'
        }
        return 'All parties'
    })()

    return (
        <>
            {/* Steps 1 + 2 — modal popup. Backdrop clicks do NOT dismiss (Scale parity).
                The base location renders the case detail underneath via the
                modal-route pattern in App.js; we apply a backdrop-blur over
                that so the case detail shows but reads as inert. */}
            {step !== 3 && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xl">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1180px] max-h-[92vh] overflow-hidden relative">
                        <button type="button" onClick={() => navigate(`/cases/${caseId}`)} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 z-50" aria-label="Close">
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 5l10 10M15 5L5 15"/></svg>
                        </button>

                        <div className="px-6 pt-5 pb-3">
                            <h2 className="text-lg font-semibold text-gray-900">Create Invoice</h2>
                            <p className="text-sm text-gray-500 mt-0.5">Select who will receive this invoice.</p>
                            {stepRow}
                        </div>

                        <div className="px-6 py-3">
                            {step === 1 && (
                                <InvoiceFirstStep
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    plaintiff={applyOverrides(bootstrap.plaintiff)}
                                    defendant={applyOverrides(bootstrap.defendant)}
                                    additionalPlaintiffParties={(bootstrap.additionalPlaintiffParties || []).map(applyOverrides)}
                                    additionalDefendantParties={(bootstrap.additionalDefendantParties || []).map(applyOverrides)}
                                    isGscCase={isGscCase}
                                    onClientChange={handleClientChange}
                                />
                            )}
                            {step === 2 && (
                                <InvoiceSecondStep
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    effectiveHourlyRate={effectiveHourlyRate}
                                    mediatorReminderSettings={mediatorSettings.hourly_invoice_reminder_settings}
                                    showAllocationToggle={showAllocation}
                                />
                            )}
                        </div>

                        <div className="px-6 pb-5 flex items-center justify-between">
                            {step === 2 ? (
                                <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
                                    ← Back
                                </button>
                            ) : <span />}
                            {step === 1 && (
                                <button
                                    type="button"
                                    disabled={persistingDraft || (values.selectedOption === 'one' && !values.selectedParty)}
                                    onClick={handleContinueFromStep1}
                                    title={values.selectedOption === 'one' && !values.selectedParty ? 'Please select a party' : ''}
                                    className="ml-auto px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50"
                                    style={{ backgroundColor: '#115E59' }}
                                >
                                    {persistingDraft ? 'Saving…' : 'Continue'}
                                </button>
                            )}
                            {step === 2 && (
                                <button
                                    type="button"
                                    disabled={persistingDraft}
                                    onClick={handlePreviewFromStep2}
                                    className="px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-60 inline-flex items-center gap-2"
                                    style={{ backgroundColor: '#115E59' }}
                                >
                                    {persistingDraft && (
                                        <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                    )}
                                    {persistingDraft ? 'Loading…' : 'Preview Invoice'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Step 3 — full-screen takeover. Wrapped in a fixed-positioned
                overlay (above the modal-route base location) so the Case
                Detail page underneath doesn't bleed through. Scrollable
                because the Summary + Preview can exceed viewport height. */}
            {step === 3 && (
                <div className="fixed inset-0 z-[60] overflow-auto bg-gray-50">
                <div className="p-6 max-w-[1600px] mx-auto space-y-4">
                    <button type="button" onClick={() => navigate(`/cases/${caseId}`)} className="text-sm text-[#115E59] mb-4 hover:underline">← Back to Case</button>
                    {/* Wider page (1280 → 1600) keeps both columns roomy: the
                        summary keeps its 4-column footprint (so the plaintiff
                        / defendant email lines no longer wrap onto a tight
                        single row), and the preview still picks up extra
                        width from the bigger page wrapper. */}
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-4 bg-gray-100 rounded">
                            <InvoiceSummary
                                values={{ ...values, subtotal }}
                                onEditRecipient={() => setStep(1)}
                                onEditDetails={() => setStep(2)}
                                plaintiff={applyOverrides(bootstrap.plaintiff)}
                                defendant={applyOverrides(bootstrap.defendant)}
                                additionalPlaintiffParties={(bootstrap.additionalPlaintiffParties || []).map(applyOverrides)}
                                additionalDefendantParties={(bootstrap.additionalDefendantParties || []).map(applyOverrides)}
                                selectedRecipient={values.selectedOption === 'one' && values.selectedParty
                                    ? allClients.find((c) => c.client_id === values.selectedParty) || null
                                    : null}
                                isAll={values.selectedOption !== 'one'}
                                adminFeeAmount={adminFeeData.adminFeeAmountNumeric}
                                adminFeeCollected={adminFeeData.isAdminFeeCollected}
                                isAdminFeeAddedInLineItem={adminFeeData.is_admin_fee_added_in_line_item}
                            />
                        </div>
                        <div className="col-span-8 bg-gray-100 rounded">
                            <InvoicePreview
                                values={{ ...values, subtotal }}
                                mediator={mediator}
                                caseData={bootstrap.case}
                                plaintiffOptions={[
                                    applyOverrides(bootstrap.plaintiff) && { value: bootstrap.plaintiff.client_id, label: applyOverrides(bootstrap.plaintiff).name },
                                    ...((bootstrap.additionalPlaintiffParties || []).map((p) => { const o = applyOverrides(p); return { value: o.client_id, label: o.name }; })),
                                ].filter(Boolean)}
                                defendantOptions={[
                                    applyOverrides(bootstrap.defendant) && { value: bootstrap.defendant.client_id, label: applyOverrides(bootstrap.defendant).name },
                                    ...((bootstrap.additionalDefendantParties || []).map((p) => { const o = applyOverrides(p); return { value: o.client_id, label: o.name }; })),
                                ].filter(Boolean)}
                                initialClient={values.selectedOption === 'one' && values.selectedParty
                                    ? allClients.find((c) => c.client_id === values.selectedParty)
                                    : (bootstrap.plaintiff && { ...applyOverrides(bootstrap.plaintiff), role: 'plaintiff' })}
                                selectClientById={(id) => allClients.find((c) => c.client_id === id) || null}
                                splitFactor={adminFeeData.splitFactor}
                                perPartyShare={adminFeeData.perPartyShare}
                                perPartyPercentage={adminFeeData.invoiceAllocationData.percentage}
                                processingFeeToClient={showProcessingFeeRow}
                                adminFee={adminFeeData.adminFeeAmountNumeric}
                                adminFeePerParty={adminFeeData.adminFeePerParty}
                                isAdminFeeAddedInLineItem={adminFeeData.is_admin_fee_added_in_line_item}
                            />
                        </div>
                    </div>

                    {/* Action bar — Scale parity (col-span-12 with white bg + border). */}
                    <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 flex justify-between bg-white border p-4 rounded">
                        <button type="button" onClick={() => setStep(2)} className="text-sm text-gray-700 hover:text-gray-900 inline-flex items-center gap-1.5">
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path d="M11 4l-6 6 6 6 1.4-1.4L7.8 10l4.6-4.6L11 4z"/></svg>
                            Back to edit
                        </button>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={submitting !== false}
                                onClick={() => handleFinalize('draft')}
                                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1.5"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                                    <polyline points="17 21 17 13 7 13 7 21"/>
                                    <polyline points="7 3 7 8 15 8"/>
                                </svg>
                                {submitting === 'draft' ? 'Saving…' : 'Save as Draft'}
                            </button>
                            {(() => {
                                // Scale parity (index.jsx:979-1003): Send Invoice is disabled
                                // when due_date is in the past. Past-date check uses end-of-day
                                // for the due date so a same-day due is still allowed.
                                const dueDateIso = values.due_date
                                let isPastDue = false
                                if (dueDateIso) {
                                    const m = /^\d{4}-\d{2}-\d{2}/.test(dueDateIso) ? dueDateIso.slice(0, 10).split('-') : null
                                    const d = m ? new Date(Number(m[0]), Number(m[1]) - 1, Number(m[2]), 23, 59, 59) : new Date(dueDateIso)
                                    isPastDue = d.getTime() < Date.now()
                                }
                                const disabled = submitting !== false || isPastDue || !values.due_date || subtotal <= 0
                                return (
                                    <button
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => handleFinalize('email')}
                                        title={isPastDue ? 'Due date cannot be in the past' : ''}
                                        className="px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50"
                                        style={{ backgroundColor: '#000' }}
                                    >
                                        {submitting === 'email' ? 'Sending…' : 'Send Invoice'}
                                    </button>
                                )
                            })()}
                        </div>
                    </div>
                    </div>
                </div>
                </div>
            )}

            {toast && (
                <div className={`fixed bottom-6 right-6 px-4 py-2 rounded-md shadow-lg text-sm z-50 ${toast.type === 'success' ? 'bg-green-600 text-white' : toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-800 text-white'}`}>
                    {toast.message}
                </div>
            )}
        </>
    )
}
